import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaSearch, FaTrash, FaCheckCircle, FaTimes,
         FaChevronLeft, FaChevronRight, FaChevronDown, FaSync,
         FaCreditCard, FaLock } from "react-icons/fa";
import {useTheme, getT, getBrandColors, lighten} from "./ThemeContext";
import API_BASE from "./apiConfig";
import { useDateTimeFormat } from "./utils/formatters";

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

// ── helpers ───────────────────────────────────────────────────────────────────
const MONTHS_L = ["January","February","March","April","May","June",
                  "July","August","September","October","November","December"];
const DAY_S    = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const LANE_COLOR = { full_lane: "#6366f1", cage: "#0ea5e9", box: "#10b981" };
const LANE_LABEL = { full_lane: "Full Lane",  cage: "Cage",  box: "Box" };
const LANE_GROUP_LABEL = { full_lane: "Lanes", cage: "Cages", box: "Boxes" };

const STATUS_COLOR = {
  confirmed:       "#10b981",
  pending_payment: "#f59e0b",
  checked_in:      "#3b82f6",
  completed:       "#7c3aed",
  cancelled:       "#6b7280",
  no_show:         "#ef4444",
};

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function timeToMins(t) {
  const [h, m] = (t || "00:00").split(":").map(Number); return h * 60 + m;
}
function minsToTime(m) {
  return `${String(Math.floor(m / 60)).padStart(2,"0")}:${String(m % 60).padStart(2,"0")}`;
}
function friendlyDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${DAY_S[d.getDay()]}, ${MONTHS_L[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
function shortDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${DAY_S[d.getDay()]} ${d.getMonth()+1}/${d.getDate()}`;
}

// parse facility operating hours for a given date
function getFacilityHours(facility, iso) {
  if (!facility?.operating_hours_json) return { openH: 6, closeH: 22 };
  try {
    const hours = JSON.parse(facility.operating_hours_json);
    const dow   = new Date(iso + "T00:00:00").getDay();
    const key   = dow === 0 ? "sunday" : dow === 6 ? "saturday" : "weekday";
    const slot  = hours[key] || { open: "06:00", close: "22:00" };
    return {
      openH:  parseInt(slot.open.split(":")[0],  10),
      closeH: parseInt(slot.close.split(":")[0], 10),
    };
  } catch { return { openH: 6, closeH: 22 }; }
}

// ── Booking Modal ─────────────────────────────────────────────────────────────
// Duration increment in minutes for each lane type
const DURATION_INCREMENT = { full_lane: 60, cage: 30, box: 60 };
// Quick duration chips per lane type
const DURATION_CHIPS = {
  full_lane: [60, 120, 180, 240],
  cage:      [30, 60, 90, 120],
  box:       [60, 120, 180, 240],
};

function BookingModal({ cell, lanes, bookingTypes, facility, T, currentUser, onClose, onBookingCreated }) {
  const { isDark } = useTheme();
  const { fmtDate, fmtTime } = useDateTimeFormat();
  const isAdmin  = currentUser?.user_role === "Admin" || currentUser?.is_superadmin;
  const isStaff  = ["Admin","Staff","Coach","Super Admin"].includes(currentUser?.user_role) || currentUser?.is_superadmin;
  const isClient = !isStaff;

  const clickedLane     = cell.lane || null;
  const clickedLaneType = clickedLane?.lane_type || "full_lane";
  const inc             = DURATION_INCREMENT[clickedLaneType] || 60;

  // Default booking type: match lane_type of clicked lane
  const defaultTypeId = (() => {
    const match = bookingTypes.find(bt => bt.lane_type === clickedLaneType);
    return match?.type_id || bookingTypes[0]?.type_id || "";
  })();

  // form
  const [typeId,    setTypeId]    = useState(defaultTypeId);
  const [laneId,    setLaneId]    = useState(clickedLane?.lane_id || lanes[0]?.lane_id || "");
  const [startMins, setStartMins] = useState(cell.hour !== undefined ? cell.hour * 60 : 9 * 60);
  const [durMins,   setDurMins]   = useState(inc); // duration in minutes
  const [academy,   setAcademy]   = useState(false);
  const [notes,     setNotes]     = useState("");

  const startTime = minsToTime(startMins);
  const endTime   = minsToTime(startMins + durMins);

  // client search
  const [search,            setSearch]            = useState("");
  const [results,           setResults]           = useState([]);
  const [selClient,         setSelClient]         = useState(null);
  const [dropOpen,          setDropOpen]          = useState(false);
  const [hoursCredit,       setHoursCredit]       = useState(null); // membership hours credit for selected client
  const dropRef = useRef(null);

  const [busy,        setBusy]       = useState(false);
  const [err,         setErr]        = useState("");
  const [success,     setSuccess]    = useState(null);
  const [recurring,   setRecurring]  = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(4);
  const [recurProgress, setRecurProgress] = useState(null); // "3/5"

  const selBT = bookingTypes.find(t => t.type_id === Number(typeId));

  // auto-set duration when booking type changes; reset lane if it doesn't match new type
  useEffect(() => {
    if (selBT?.duration_minutes) setDurMins(selBT.duration_minutes);
    if (selBT) {
      const currentLane = lanes.find(l => l.lane_id === Number(laneId));
      if (currentLane && currentLane.lane_type !== selBT.lane_type) {
        setLaneId("");
      }
    }
  }, [typeId]); // eslint-disable-line

  // for client login — auto-load their own record from lb_clients by email
  useEffect(() => {
    if (isClient && currentUser?.email) {
      fetch(`${BASE}/lb/clients?search=${encodeURIComponent(currentUser.email)}&status=active`)
        .then(r => r.json())
        .then(d => {
          const match = (d.clients || []).find(c =>
            c.email?.toLowerCase() === currentUser.email?.toLowerCase()
          );
          if (match) setSelClient(match);
        });
    }
  }, [isClient, currentUser?.email]); // eslint-disable-line

  // fetch membership hours credit when a member client is selected
  useEffect(() => {
    if (selClient?.client_id && selClient?.membership_status === "active") {
      fetch(`${BASE}/lb/clients/${selClient.client_id}/billing-cycle`)
        .then(r => r.json())
        .then(d => setHoursCredit(d.hours_credit || null))
        .catch(() => setHoursCredit(null));
    } else {
      setHoursCredit(null);
    }
  }, [selClient?.client_id]); // eslint-disable-line

  // admin search debounce — searches both lb_clients (customers) and students
  useEffect(() => {
    if (!isStaff || !search || selClient || academy) { setResults([]); return; }
    const t = setTimeout(() => {
      const params = new URLSearchParams({ q: search });
      fetch(`${BASE}/lb/search-clients?${params}`)
        .then(r => r.json()).then(d => { setResults(d.clients || []); setDropOpen(true); });
    }, 260);
    return () => clearTimeout(t);
  }, [search, selClient, isStaff, academy]);

  useEffect(() => {
    const fn = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // pricing — an active enrolled student also qualifies for member lane pricing
  // even without a separately-purchased lb_client_memberships plan (mirrors the
  // backend's is_member check in POST /lb/bookings — see lane_booking.py).
  const isMember   = selClient?.membership_status === "active"
    || (selClient?.roles?.includes("student") && selClient?.student_active_status === 1);
  const durationM  = timeToMins(endTime) - timeToMins(startTime);
  const basePrice  = academy ? 0 : parseFloat(selBT ? (isMember ? selBT.member_price : selBT.non_member_price) : 0);
  // Pro-rate if duration differs from booking type duration
  const btDuration = selBT?.duration_minutes || 60;
  const proRated   = btDuration > 0 ? basePrice * (durationM / btDuration) : basePrice;
  const tax        = academy ? 0 : proRated * parseFloat(selBT?.tax_rate || 0) / 100;
  const fee        = academy ? 0 : parseFloat(selBT?.service_fee || 0);
  const total      = Math.max(0, Math.round((proRated + tax + fee) * 100) / 100);

  // ── Hours-credit logic for member clients ────────────────────────────────────
  const durationHrs      = durationM / 60;
  const hcAvail          = hoursCredit ? hoursCredit.remaining_hours : null;  // null = no plan limit
  const hcIncluded       = hoursCredit ? hoursCredit.hours_per_month : null;
  const hcUsed           = hoursCredit ? hoursCredit.used_hours : null;
  // how many hours of this booking are covered vs chargeable
  const freeHrs          = (isMember && hcAvail !== null) ? Math.min(durationHrs, Math.max(0, hcAvail)) : 0;
  const chargeHrs        = (isMember && hcAvail !== null) ? Math.max(0, durationHrs - hcAvail) : (isMember ? 0 : durationHrs);
  const unitPriceMember  = parseFloat(selBT ? selBT.member_price : 0);
  const btDurationHrs    = btDuration / 60;
  const overageCharge    = btDurationHrs > 0 ? Math.round((unitPriceMember / btDurationHrs) * chargeHrs * 100) / 100 : 0;
  // booking states for member
  const memberFullyCovered  = isMember && hoursCredit && hcAvail !== null && chargeHrs === 0 && hcAvail > 0;
  const memberPartialOverflow = isMember && hoursCredit && hcAvail !== null && chargeHrs > 0 && hcAvail > 0;
  const memberFullyExhausted  = isMember && hoursCredit && hcAvail !== null && hcAvail <= 0;
  const effectiveTotal   = isMember && hoursCredit ? overageCharge : total;

  const makePayload = (dateStr) => ({
    client_id:          academy ? null : (selClient?.client_id || null),
    student_id:         academy ? null : (selClient?.student_id || null),
    lane_id:            Number(laneId),
    booking_type_id:    Number(typeId),
    booking_date:       dateStr,
    start_time:         startTime,
    end_time:           endTime,
    is_academy_booking: academy,
    booked_by_type:     isStaff ? "admin" : "client",
    booked_by_id:       currentUser?.user_id || 0,
    notes,
  });

  const save = async () => {
    if (!academy && !selClient) { setErr("Please select a client."); return; }
    if (!laneId)    { setErr("Please select a lane."); return; }
    if (!typeId)    { setErr("Please select a booking type."); return; }
    if (durationM <= 0) { setErr("End time must be after start time."); return; }
    setBusy(true); setErr("");

    const weeks = recurring ? repeatWeeks : 1;
    const created = [];

    for (let w = 0; w < weeks; w++) {
      const bookDate = addDays(cell.date, w * 7);
      if (recurring) setRecurProgress(`${w + 1} / ${weeks}`);
      const res = await fetch(`${BASE}/lb/bookings`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(makePayload(bookDate)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(`Week ${w + 1} (${bookDate}): ${data.detail || "Booking failed."}`);
        setBusy(false); setRecurProgress(null); return;
      }
      created.push({ data, bookDate });
      // Optimistic insert for the currently-viewed date only
      if (bookDate === cell.date) {
        onBookingCreated({
          booking_id:         data.booking_id,
          booking_ref:        data.booking_ref,
          lane_id:            Number(laneId),
          booking_date:       bookDate,
          start_time:         startTime,
          end_time:           endTime,
          status:             academy ? "confirmed" : (data.status || "confirmed"),
          client_name:        selClient ? `${selClient.first_name} ${selClient.last_name}` : "Academy",
          type_name:          bookingTypes.find(t => t.type_id === Number(typeId))?.type_name || "",
          total_amount:       String(total),
          is_academy_booking: academy ? 1 : 0,
        });
      }
    }

    setRecurProgress(null);
    setSuccess({ ...created[0].data, recurCount: created.length });
    setBusy(false);
  };

  // ─── shared micro-styles ────────────────────────────────────────────────────
  const inp = {
    padding: "8px 11px", border: `1.5px solid ${T.inputBdr}`,
    borderRadius: 8, fontSize: 13, fontFamily: FONT,
    outline: "none", background: T.inputBg, color: T.inputText,
    boxSizing: "border-box", width: "100%",
  };
  const fieldLbl = {
    fontSize: 10, fontWeight: 800, textTransform: "uppercase",
    letterSpacing: "0.07em", color: T.textLabel, fontFamily: FONT,
    display: "block", marginBottom: 4,
  };
  const divider = <div style={{ height: 1, background: T.divider, margin: "0 -22px" }} />;

  // ─── derived display values ──────────────────────────────────────────────────
  const laneObj   = lanes.find(l => l.lane_id === Number(laneId));
  const laneName  = laneObj?.lane_name || clickedLane?.lane_name || "—";
  const laneType  = laneObj?.lane_type || clickedLaneType;
  const laneColor = laneObj?.color || LANE_COLOR[laneType] || "#6366f1";
  const chips     = DURATION_CHIPS[laneType] || DURATION_CHIPS.full_lane;
  const durLabel  = durMins >= 60
    ? (durMins % 60 === 0 ? `${durMins/60}h` : `${Math.floor(durMins/60)}h ${durMins%60}m`)
    : `${durMins}m`;
  const bookingDateStr = cell.date ? fmtDate(cell.date) : "";

  const STEPS = ["Client","Type & Lane","Session","Billing","Confirm"];
  const canConfirm = (academy || !!selClient) && !!laneId && !!typeId && durMins > 0;

  const confirmLabel = (() => {
    if (recurProgress)         return `Booking week ${recurProgress}…`;
    if (busy)                  return "Processing…";
    if (academy && recurring)  return `Confirm ${repeatWeeks} Academy Bookings`;
    if (academy)               return "Confirm Academy Booking — $0.00";
    if (memberFullyCovered)    return "Confirm — Covered by Membership";
    if (memberPartialOverflow) return `Confirm & Pay $${overageCharge.toFixed(2)}`;
    if (memberFullyExhausted)  return `Confirm & Pay $${overageCharge.toFixed(2)} (member rate)`;
    if (recurring && selClient?.card_last4)
                               return `Pay ${repeatWeeks}× $${effectiveTotal.toFixed(2)} = $${(effectiveTotal*repeatWeeks).toFixed(2)}`;
    if (recurring)             return `Confirm ${repeatWeeks} Bookings — $${(effectiveTotal*repeatWeeks).toFixed(2)}`;
    if (selClient?.card_last4) return `Confirm & Pay $${effectiveTotal.toFixed(2)}`;
    return `Confirm Booking — $${effectiveTotal.toFixed(2)} (pending payment)`;
  })();

  // ─── step indicator bar ─────────────────────────────────────────────────────
  const StepBar = () => (
    <div style={{ display:"flex", alignItems:"center", padding:"12px 22px 0", gap:0 }}>
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: `linear-gradient(135deg,${getBrandColors().blue},${lighten(getBrandColors().blue,50)})`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize: 10, fontWeight: 900, color: "#fff",
              fontFamily: FONT, flexShrink: 0,
            }}>{i + 1}</div>
            <span style={{ fontSize: 9, fontWeight: 700,
                           color: isDark ? "#93c5fd" : getBrandColors().blue,
                           fontFamily: FONT, letterSpacing:"0.04em", textTransform:"uppercase",
                           whiteSpace:"nowrap" }}>{label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex: 1, height: 2, background: isDark?"rgba(255,255,255,0.08)":"rgba(3,78,123,0.10)",
                          margin:"0 4px", marginBottom:16 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.62)", zIndex:1000,
                  display:"flex", alignItems:"center", justifyContent:"center", padding:12 }}>
      <div style={{
        background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
        border: T.glassBdr, borderRadius: 18, boxShadow: T.glassSh,
        width: 600, maxHeight: "95vh", overflowY: "auto",
        display: "flex", flexDirection: "column",
      }}>

        {/* ── Compact one-line header ── */}
        <div style={{ padding:"14px 20px 10px", borderBottom:`1px solid ${T.divider}`,
                      display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
          <div style={{ flex:1, display:"flex", alignItems:"center", gap:8, minWidth:0, flexWrap:"wrap" }}>
            <span style={{ fontSize:14, fontWeight:800, color:T.text, fontFamily:FONT, whiteSpace:"nowrap" }}>
              New Booking
            </span>
            <span style={{ color: isDark?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", fontSize:14 }}>—</span>
            <span style={{ fontSize:12, fontWeight:700, color:laneColor,
                           background:`${laneColor}18`, borderRadius:5, padding:"2px 8px",
                           fontFamily:FONT, whiteSpace:"nowrap" }}>
              {laneName} · {LANE_LABEL[laneType]}
            </span>
            <span style={{ color: isDark?"rgba(255,255,255,0.2)":"rgba(0,0,0,0.15)", fontSize:14 }}>—</span>
            <span style={{ fontSize:12, color:T.textMuted, fontFamily:FONT, whiteSpace:"nowrap" }}>
              {bookingDateStr}
            </span>
          </div>
          <button onClick={onClose}
            style={{ background:"none", border:"none", cursor:"pointer",
                     color:T.textMuted, padding:4, flexShrink:0 }}>
            <FaTimes size={14} />
          </button>
        </div>

        {/* ── Step indicator ── */}
        <StepBar />

        {success ? (
          <div style={{ padding:"36px 24px", textAlign:"center" }}>
            <FaCheckCircle size={44} color="#10b981" style={{ marginBottom:12 }} />
            <div style={{ fontSize:18, fontWeight:900, color:T.text, fontFamily:FONT, marginBottom:6 }}>
              Booking Confirmed!
            </div>
            <div style={{ display:"inline-block", padding:"4px 16px", borderRadius:20,
                          background:"rgba(16,185,129,0.12)", color:"#10b981",
                          fontSize:12, fontWeight:700, fontFamily:FONT, marginBottom:10 }}>
              {success.booking_ref}
            </div>
            <div style={{ fontSize:12, color:T.textMuted, fontFamily:FONT, marginBottom:3 }}>
              {laneName} · {fmtTime(startTime)} – {fmtTime(endTime)} · {durLabel}
            </div>
            <div style={{ fontSize:14, fontWeight:800, color:T.text, fontFamily:FONT, marginBottom:3 }}>
              {academy ? "Academy — $0.00" : `$${parseFloat(success.total_amount||total).toFixed(2)}`}
            </div>
            {success.recurCount > 1 && (
              <div style={{ fontSize:11, color:T.textMuted, fontFamily:FONT, marginBottom:20 }}>
                {success.recurCount} weekly bookings created
              </div>
            )}
            <button onClick={onClose}
              style={{ padding:"10px 30px", borderRadius:10, border:"none", cursor:"pointer",
                       background:"linear-gradient(135deg,#ffd700,#fff3a0)",
                       color:getBrandColors().blueDark, fontSize:13, fontWeight:800, fontFamily:FONT }}>
              Done
            </button>
          </div>
        ) : (
          <div style={{ padding:"16px 20px 18px", display:"flex", flexDirection:"column", gap:0 }}>

            {err && (
              <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:12,
                            background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)",
                            color:"#ef4444", fontSize:12, fontFamily:FONT }}>{err}</div>
            )}

            {/* ══ 1. Client search ════════════════════════════════════════ */}
            <div style={{ marginBottom:14 }}>
              {/* Label row: "Search Client" + Academy toggle on same line */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                <span style={fieldLbl}>Search Client</span>
                {isAdmin && (
                  <label style={{ display:"flex", alignItems:"center", gap:5, cursor:"pointer" }}>
                    <input type="checkbox" checked={academy}
                      onChange={e => {
                        setAcademy(e.target.checked);
                        if (e.target.checked) { setSelClient(null); setSearch(""); setResults([]); }
                      }}
                      style={{ width:12, height:12, accentColor:"#6366f1" }} />
                    <span style={{ fontSize:11, fontWeight:600, color:"#6366f1", fontFamily:FONT }}>
                      Academy (no charge)
                    </span>
                  </label>
                )}
              </div>

              {academy ? (
                <div style={{ padding:"8px 12px", borderRadius:8,
                              background:"rgba(99,102,241,0.09)", border:"1px solid rgba(99,102,241,0.22)",
                              fontSize:12, color:"#818cf8", fontWeight:600, fontFamily:FONT }}>
                  Academy booking — no client required
                </div>
              ) : isStaff ? (
                <div ref={dropRef} style={{ position:"relative" }}>
                  {!selClient ? (
                    <>
                      <FaSearch size={11} style={{ position:"absolute", left:11, top:"50%",
                                                    transform:"translateY(-50%)", color:T.textMuted,
                                                    pointerEvents:"none" }} />
                      <input
                        value={search}
                        onChange={e => { setSearch(e.target.value); setSelClient(null); }}
                        onFocus={() => results.length && setDropOpen(true)}
                        placeholder="Name, email or phone…"
                        style={{ ...inp, paddingLeft:30 }} />
                    </>
                  ) : (
                    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px",
                                  borderRadius:8, border:`1.5px solid ${laneColor}44`,
                                  background:`${laneColor}0c` }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:T.text, fontFamily:FONT }}>
                          {selClient.first_name} {selClient.last_name}
                        </div>
                        <div style={{ fontSize:11, color:T.textMuted, fontFamily:FONT, marginTop:1 }}>
                          {selClient.email}
                          {selClient.membership_status==="active" && (
                            <span style={{ marginLeft:7, color:"#6366f1", fontWeight:700 }}>· Member</span>
                          )}
                          {selClient.card_last4 && (
                            <span style={{ marginLeft:7, color:"#10b981", fontWeight:700 }}>
                              · ••{selClient.card_last4}
                            </span>
                          )}
                        </div>
                      </div>
                      <button onClick={() => { setSelClient(null); setSearch(""); }}
                        style={{ background:"none", border:"none", cursor:"pointer",
                                 color:T.textMuted, padding:2, display:"flex" }}>
                        <FaTimes size={11} />
                      </button>
                    </div>
                  )}
                  {/* Dropdown */}
                  {dropOpen && results.length > 0 && !selClient && (
                    <div style={{ position:"absolute", top:"calc(100% + 3px)", left:0, right:0,
                                  background:T.glass, backdropFilter:BLUR, WebkitBackdropFilter:BLUR,
                                  border:T.glassBdr, borderRadius:10, boxShadow:T.glassSh,
                                  zIndex:30, maxHeight:200, overflowY:"auto" }}>
                      {results.map(c => {
                        const roles = c.roles || [];
                        const badges = [
                          roles.includes("student") && { label:"Student", bg:"rgba(16,185,129,0.13)", color:"#10b981" },
                          roles.includes("member")  && { label:"Member",  bg:"rgba(99,102,241,0.12)", color:"#6366f1" },
                        ].filter(Boolean);
                        return (
                          <div key={`c-${c.email||c.client_id}`}
                            onMouseDown={() => { setSelClient(c); setSearch(""); setDropOpen(false); setResults([]); }}
                            style={{ padding:"9px 14px", cursor:"pointer", borderBottom:`1px solid ${T.divider}` }}
                            onMouseEnter={e => e.currentTarget.style.background = T.inputBg}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap" }}>
                              <span style={{ fontSize:13, fontWeight:700, color:T.text, fontFamily:FONT }}>
                                {c.first_name} {c.last_name}
                              </span>
                              {badges.map(b => (
                                <span key={b.label} style={{ fontSize:9, fontWeight:800, borderRadius:4,
                                  padding:"2px 6px", textTransform:"uppercase", background:b.bg, color:b.color }}>
                                  {b.label}
                                </span>
                              ))}
                            </div>
                            <div style={{ fontSize:11, color:T.textMuted, fontFamily:FONT, marginTop:1 }}>
                              {c.email}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                selClient ? (
                  <div style={{ padding:"8px 12px", borderRadius:8, background:T.inputBg,
                                border:`1.5px solid ${T.inputBdr}`, fontSize:13, fontFamily:FONT, color:T.text }}>
                    <span style={{ fontWeight:700 }}>{selClient.first_name} {selClient.last_name}</span>
                    <span style={{ color:T.textMuted, marginLeft:8 }}>{selClient.email}</span>
                  </div>
                ) : (
                  <div style={{ padding:"8px 12px", borderRadius:8,
                                background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.3)",
                                fontSize:12, color:"#f59e0b", fontFamily:FONT }}>
                    No client account linked — contact admin.
                  </div>
                )
              )}
            </div>

            {divider}

            {/* ══ 2. Type & Lane (same row) ══════════════════════════════ */}
            <div style={{ margin:"14px 0" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <label style={fieldLbl}>Booking Type</label>
                  <select value={typeId} onChange={e => setTypeId(e.target.value)} style={inp}>
                    <option value="">— Select —</option>
                    {bookingTypes.map(bt => (
                      <option key={bt.type_id} value={bt.type_id}>{bt.type_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={fieldLbl}>Lane / Court</label>
                  <select value={laneId} onChange={e => setLaneId(e.target.value)} style={inp}>
                    <option value="">— Select —</option>
                    {lanes
                      .filter(l => !selBT || l.lane_type === selBT.lane_type)
                      .map(l => (
                        <option key={l.lane_id} value={l.lane_id}>
                          {l.lane_name} ({LANE_LABEL[l.lane_type]})
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            {divider}

            {/* ══ 3. Session time — Start | Duration | End ══════════════ */}
            <div style={{ margin:"14px 0" }}>
              <div style={{ display:"grid", gridTemplateColumns:"auto 1fr auto", alignItems:"end", gap:10 }}>

                {/* Start Time */}
                <div>
                  <label style={fieldLbl}>Start Time</label>
                  <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                    <button onClick={() => setStartMins(m => Math.max(0, m - inc))}
                      style={{ width:26, height:34, borderRadius:6, border:`1px solid ${T.inputBdr}`,
                               background:"none", color:T.text, cursor:"pointer", fontSize:14,
                               display:"flex", alignItems:"center", justifyContent:"center" }}>−</button>
                    <input type="time" value={startTime}
                      onChange={e => { const [h,m]=e.target.value.split(":").map(Number); setStartMins(h*60+m); }}
                      style={{ ...inp, width:98, textAlign:"center", padding:"7px 6px" }} />
                    <button onClick={() => setStartMins(m => Math.min(23*60, m + inc))}
                      style={{ width:26, height:34, borderRadius:6, border:`1px solid ${T.inputBdr}`,
                               background:"none", color:T.text, cursor:"pointer", fontSize:14,
                               display:"flex", alignItems:"center", justifyContent:"center" }}>+</button>
                  </div>
                </div>

                {/* Duration chips */}
                <div>
                  <label style={fieldLbl}>Duration</label>
                  <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"nowrap" }}>
                    {chips.map(c => {
                      const cl = c>=60 ? (c%60===0?`${c/60}h`:`${Math.floor(c/60)}h${c%60}m`) : `${c}m`;
                      return (
                        <button key={c} onClick={() => setDurMins(c)}
                          style={{ padding:"6px 10px", borderRadius:6, border:"none", cursor:"pointer",
                                   fontSize:11, fontWeight:700, fontFamily:FONT, flexShrink:0,
                                   background: durMins===c ? laneColor : (isDark?"rgba(255,255,255,0.08)":"rgba(3,78,123,0.08)"),
                                   color: durMins===c ? "#fff" : T.textMuted,
                                   transition:"all 0.1s" }}>
                          {cl}
                        </button>
                      );
                    })}
                    <button onClick={() => setDurMins(m => Math.max(inc, m-inc))}
                      style={{ width:26, height:34, borderRadius:6, border:`1px solid ${T.inputBdr}`,
                               background:"none", color:T.textMuted, cursor:"pointer", fontSize:14,
                               display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>−</button>
                    <button onClick={() => setDurMins(m => m+inc)}
                      style={{ width:26, height:34, borderRadius:6, border:`1px solid ${T.inputBdr}`,
                               background:"none", color:T.textMuted, cursor:"pointer", fontSize:14,
                               display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>+</button>
                  </div>
                </div>

                {/* End Time */}
                <div>
                  <label style={fieldLbl}>End Time</label>
                  <div style={{ padding:"7px 14px", borderRadius:7, height:34, boxSizing:"border-box",
                                background:`${laneColor}12`, border:`1.5px solid ${laneColor}40`,
                                fontSize:13, fontWeight:800, color:laneColor, fontFamily:FONT,
                                whiteSpace:"nowrap", display:"flex", alignItems:"center" }}>
                    {fmtTime(endTime)}
                  </div>
                </div>

              </div>

              {/* Repeat weekly — same row below */}
              <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <label style={{ display:"flex", alignItems:"center", gap:5, cursor:"pointer" }}>
                  <input type="checkbox" checked={recurring}
                    onChange={e => setRecurring(e.target.checked)}
                    style={{ width:12, height:12, accentColor:"#6366f1" }} />
                  <span style={{ fontSize:11, fontWeight:600, color:T.textMuted, fontFamily:FONT }}>
                    Repeat weekly
                  </span>
                </label>
                {recurring && (
                  <>
                    <span style={{ fontSize:11, color:T.textMuted, fontFamily:FONT }}>for</span>
                    <input type="number" min={2} max={52} value={repeatWeeks}
                      onChange={e => setRepeatWeeks(Math.max(2,Math.min(52,Number(e.target.value)||2)))}
                      style={{ ...inp, width:50, padding:"4px 8px", fontSize:12 }} />
                    <span style={{ fontSize:11, color:"#6366f1", fontFamily:FONT, fontWeight:700 }}>
                      wks = {repeatWeeks} bookings
                    </span>
                  </>
                )}
              </div>
            </div>

            {divider}

            {/* ══ 4. Billing summary ══════════════════════════════════════ */}
            <div style={{ margin:"14px 0" }}>
              <label style={fieldLbl}>Billing Summary</label>

              {academy ? (
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                              padding:"8px 12px", borderRadius:8,
                              background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.20)" }}>
                  <span style={{ fontSize:12, color:"#818cf8", fontFamily:FONT }}>Academy — no charge</span>
                  <span style={{ fontSize:16, fontWeight:900, color:"#6366f1", fontFamily:FONT }}>$0.00</span>
                </div>
              ) : isMember && hoursCredit && durMins > 0 ? (
                <>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:8 }}>
                    {[
                      { label:"Included/Mo", value:`${hcIncluded}h`, color:"#6366f1" },
                      { label:"Used",        value:`${hcUsed}h`,     color:hcUsed>=hcIncluded?"#ef4444":"#f59e0b" },
                      { label:"Remaining",   value:`${hcAvail}h`,    color:hcAvail<=0?"#ef4444":"#10b981" },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ textAlign:"center", padding:"6px 4px",
                        background:"rgba(99,102,241,0.06)", borderRadius:7,
                        border:"1px solid rgba(99,102,241,0.12)" }}>
                        <div style={{ fontSize:8, fontWeight:800, textTransform:"uppercase",
                          color:"#6366f1", fontFamily:FONT, marginBottom:2 }}>{label}</div>
                        <div style={{ fontSize:14, fontWeight:900, color, fontFamily:FONT }}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {memberFullyCovered && (
                    <div style={{ padding:"7px 11px", borderRadius:7, fontSize:11, fontWeight:700,
                                  color:"#10b981", background:"rgba(16,185,129,0.08)",
                                  border:"1px solid rgba(16,185,129,0.25)", fontFamily:FONT }}>
                      ✓ Fully covered — {Math.max(0,hcAvail-durationHrs).toFixed(1)}h remaining after.
                    </div>
                  )}
                  {(memberPartialOverflow || memberFullyExhausted) && (
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                                  padding:"7px 11px", borderRadius:7,
                                  background:"rgba(245,158,11,0.09)", border:"1px solid rgba(245,158,11,0.30)" }}>
                      <span style={{ fontSize:11, color:"#f59e0b", fontFamily:FONT, fontWeight:600 }}>
                        {memberPartialOverflow ? `${chargeHrs.toFixed(1)}h over limit` : "Hours exhausted"}
                      </span>
                      <span style={{ fontSize:15, fontWeight:900, color:"#f59e0b", fontFamily:FONT }}>
                        ${overageCharge.toFixed(2)}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {selBT && durMins > 0 && (
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12,
                                  fontFamily:FONT, marginBottom:4 }}>
                      <span style={{ color:T.textMuted }}>
                        {selBT.type_name}{isMember?" (Member)":""} · {durLabel}
                      </span>
                      <span style={{ fontWeight:700, color:T.text }}>${proRated.toFixed(2)}</span>
                    </div>
                  )}
                  {tax > 0 && (
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:11,
                                  fontFamily:FONT, marginBottom:4 }}>
                      <span style={{ color:T.textMuted }}>Tax ({selBT?.tax_rate}%)</span>
                      <span style={{ color:T.textMuted }}>${tax.toFixed(2)}</span>
                    </div>
                  )}
                  {fee > 0 && (
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:11,
                                  fontFamily:FONT, marginBottom:4 }}>
                      <span style={{ color:T.textMuted }}>Service fee</span>
                      <span style={{ color:T.textMuted }}>${fee.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                                paddingTop:8, borderTop:`1px solid ${T.divider}`, marginTop:4 }}>
                    <span style={{ fontSize:13, fontWeight:800, color:T.text, fontFamily:FONT }}>Total</span>
                    <span style={{ fontSize:22, fontWeight:900, color:"#ffd700",
                                   fontFamily:FONT, letterSpacing:"-0.02em" }}>
                      ${total.toFixed(2)}
                    </span>
                  </div>
                  {selClient?.card_last4 && (
                    <div style={{ display:"flex", alignItems:"center", gap:7, marginTop:8,
                                  padding:"7px 11px", borderRadius:7,
                                  background:"rgba(16,185,129,0.07)", border:"1px solid rgba(16,185,129,0.22)" }}>
                      <FaCreditCard size={12} color="#10b981" />
                      <span style={{ fontSize:12, fontWeight:700, color:T.text, fontFamily:FONT }}>
                        {(selClient.card_brand||"Card").toUpperCase()} •••• {selClient.card_last4}
                      </span>
                      <span style={{ marginLeft:"auto", fontSize:10, fontWeight:700, color:"#10b981",
                                     display:"flex", alignItems:"center", gap:3 }}>
                        <FaLock size={8} /> Will be charged
                      </span>
                    </div>
                  )}
                  {selClient && !selClient.card_last4 && (
                    <div style={{ marginTop:7, padding:"6px 10px", borderRadius:7,
                                  background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)",
                                  fontSize:11, color:"#f59e0b", fontFamily:FONT }}>
                      No card on file — pending payment
                    </div>
                  )}
                </>
              )}

              {/* Notes inline */}
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="Notes for schedulers (optional)…"
                style={{ ...inp, resize:"vertical", minHeight:46, marginTop:10, fontSize:12 }} />
            </div>

            {divider}

            {/* ══ 5. Confirm button ═══════════════════════════════════════ */}
            <div style={{ marginTop:14 }}>
              <button onClick={save} disabled={busy || !canConfirm}
                style={{
                  width:"100%", padding:"13px", borderRadius:12, border:"none",
                  cursor: (busy||!canConfirm) ? "not-allowed" : "pointer",
                  background: !canConfirm
                    ? (isDark?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)")
                    : academy
                    ? "linear-gradient(135deg,#6366f1,#818cf8)"
                    : memberFullyCovered
                    ? `linear-gradient(135deg,${lighten(getBrandColors().green,20)},${lighten(getBrandColors().green,35)})`
                    : `linear-gradient(135deg,${getBrandColors().blue},${lighten(getBrandColors().blue,50)})`,
                  color: !canConfirm ? T.textMuted : "#fff",
                  fontSize: 14, fontWeight: 800, fontFamily: FONT,
                  opacity: busy ? 0.7 : 1, transition:"all 0.15s",
                  boxShadow: !canConfirm||busy ? "none"
                    : memberFullyCovered ? "0 4px 16px rgba(16,185,129,0.30)"
                    : "0 4px 20px rgba(3,78,123,0.35)",
                  letterSpacing:"0.01em",
                }}>
                {confirmLabel}
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

// ── View booking detail modal ─────────────────────────────────────────────────
function ViewBookingModal({ booking: bProp, T, onClose, onUpdateStatus }) {
  const { fmtTime } = useDateTimeFormat();
  const [busy,    setBusy]   = useState(false);
  const [booking, setBooking] = useState(bProp); // local copy so status updates show instantly
  const sc = STATUS_COLOR[booking.status] || "#6366f1";

  const act = async (endpoint, newStatus, body = {}) => {
    setBusy(true);
    const res = await fetch(`${BASE}/lb/bookings/${booking.booking_id}/${endpoint}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    setBusy(false);
    if (res.ok) {
      // Optimistically update local status — no re-fetch needed
      const updated = { ...booking, status: newStatus };
      setBooking(updated);
      onUpdateStatus(booking.booking_id, newStatus); // update parent grid cell color
      if (newStatus === "cancelled") onClose();       // close on cancel
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                    border: T.glassBdr, borderRadius: 20, boxShadow: T.glassSh,
                    width: 420, padding: "24px 28px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: FONT }}>
              {booking.booking_ref}
            </div>
            <div style={{ marginTop: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: sc, background: `${sc}22`,
                             borderRadius: 20, padding: "3px 14px", fontFamily: FONT }}>
                {booking.status?.replace(/_/g," ")}
              </span>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted }}>
            <FaTimes size={16} />
          </button>
        </div>

        {[
          ["Client",  booking.client_name || "Academy"],
          ["Lane",    booking.lane_name],
          ["Date",    booking.booking_date],
          ["Time",    `${fmtTime(booking.start_time)} – ${fmtTime(booking.end_time)}`],
          ["Amount",  `$${parseFloat(booking.total_amount || 0).toFixed(2)}`],
        ].map(([l, v]) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between",
                                 padding: "9px 0", borderBottom: `1px solid ${T.divider}`,
                                 fontSize: 13, fontFamily: FONT }}>
            <span style={{ color: T.textMuted }}>{l}</span>
            <span style={{ fontWeight: 600, color: T.text }}>{v}</span>
          </div>
        ))}

        <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
          {booking.status === "confirmed" && (
            <button onClick={() => act("checkin", "checked_in")} disabled={busy}
              style={{ padding: "9px 18px", borderRadius: 9, border: "none", cursor: "pointer",
                       background: "rgba(59,130,246,0.14)", color: "#3b82f6",
                       fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
              Check In
            </button>
          )}
          {!["cancelled","completed","checked_in"].includes(booking.status) && (
            <button onClick={() => act("cancel", "cancelled", { reason: "Admin cancel", cancelled_by: "admin", issue_refund: true })}
              disabled={busy}
              style={{ padding: "9px 18px", borderRadius: 9, border: "none", cursor: "pointer",
                       background: "rgba(239,68,68,0.12)", color: "#ef4444",
                       fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
              Cancel
            </button>
          )}
          {booking.status === "checked_in" && (
            <div style={{ fontSize: 12, color: "#10b981", fontWeight: 700, fontFamily: FONT,
                          display: "flex", alignItems: "center", gap: 6 }}>
              ✓ Checked in
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main grid ─────────────────────────────────────────────────────────────────
export default function LaneBookingPortal({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtTime } = useDateTimeFormat();
  const hourLabel = (h) => fmtTime(`${String(h).padStart(2, "0")}:00`);

  const isAdmin = currentUser?.user_role === "Admin" || currentUser?.is_superadmin;
  const isStaff = ["Admin","Staff","Coach","Super Admin"].includes(currentUser?.user_role)
               || currentUser?.is_superadmin;

  const [facilities,   setFacilities]   = useState([]);
  const [selFacility,  setSelFacility]  = useState(null);
  const [lanes,        setLanes]        = useState([]);
  const [bookingTypes, setBookingTypes] = useState([]);
  const [bookings,     setBookings]     = useState([]);
  const [conflicts,    setConflicts]    = useState({}); // lane_id → [conflicted_lane_ids]
  const [selDate,      setSelDate]      = useState(isoToday());
  const [loading,      setLoading]      = useState(false);
  const [modal,        setModal]        = useState(null);
  // Tree-collapse state for the Lanes/Cages/Boxes grouping — purely a
  // display convenience (which columns are visible), never restricts which
  // lanes can actually be booked (BookingModal always receives the full
  // lane list regardless of what's collapsed here).
  const [collapsedTypes, setCollapsedTypes] = useState({});
  const toggleGroup = (type) => setCollapsedTypes(prev => ({ ...prev, [type]: !prev[type] }));

  // Live "now" marker for today's view — recomputed every 30s so the grid
  // reads as a live, current schedule rather than a static screenshot.
  const [nowTick, setNowTick] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNowTick(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  // ── load facilities + booking types on mount
  useEffect(() => {
    fetch(`${BASE}/lb/facilities`).then(r => r.json()).then(d => {
      const list = d.facilities || [];
      setFacilities(list);
      if (list.length) setSelFacility(list[0]);
    });
    fetch(`${BASE}/lb/booking-types`).then(r => r.json())
      .then(d => setBookingTypes(d.booking_types || []));
  }, []);

  // ── load lanes when facility changes
  useEffect(() => {
    if (!selFacility) return;
    fetch(`${BASE}/lb/lanes?facility_id=${selFacility.facility_id}`)
      .then(r => r.json()).then(d => setLanes(d.lanes || []));
  }, [selFacility]);

  // ── load day bookings + conflict map
  const loadBookings = useCallback(() => {
    if (!selFacility) return;
    setLoading(true);
    fetch(`${BASE}/lb/calendar?facility_id=${selFacility.facility_id}&view_date=${selDate}&view=day`)
      .then(r => r.json())
      .then(d => {
        setBookings(d.bookings || []);
        setConflicts(d.conflicts || {});
        setLoading(false);
      })
      .catch(() => { setBookings([]); setLoading(false); });
  }, [selFacility, selDate]);

  // ── optimistic booking insert — no background sync to avoid race with DB write
  const handleBookingCreated = useCallback((newBooking) => {
    setBookings(prev => [...prev, newBooking]);
  }, []);

  // ── optimistic status update (check-in, cancel) — updates grid cell colour instantly
  const handleStatusUpdate = useCallback((bookingId, newStatus) => {
    setBookings(prev => prev.map(b =>
      b.booking_id === bookingId ? { ...b, status: newStatus } : b
    ));
  }, []);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  // ── operating hours for the selected date
  const { openH, closeH } = getFacilityHours(selFacility, selDate);

  // admin can book anytime (6AM–11PM); clients restricted to facility hours
  const HOURS = isAdmin
    ? Array.from({ length: 17 }, (_, i) => i + 6)           // 6 AM – 10 PM
    : Array.from({ length: closeH - openH }, (_, i) => i + openH);

  // ── advance booking restriction
  // Admin/Staff: 365 days ahead
  // Member/Parent (lane booking): current week Mon–Sun + next full week (Sun of next week)
  const getEndOfWeek = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const day = d.getDay(); // 0=Sun
    const toSun = day === 0 ? 0 : 7 - day;
    return addDays(dateStr, toSun);
  };
  const todayStr = isoToday();
  const endOfNextWeek = addDays(getEndOfWeek(todayStr), 7);
  const maxDate = isStaff ? addDays(todayStr, 365) : endOfNextWeek;

  const navigate = (dir) => {
    const next = addDays(selDate, dir);
    if (!isStaff && next > maxDate) return;
    setSelDate(next);
  };

  // ── grid helpers
  const bookingAt = (lane_id, hour) =>
    bookings.find(b =>
      b.lane_id === lane_id &&
      timeToMins(b.start_time) <= hour * 60 &&
      timeToMins(b.end_time)   >  hour * 60 &&
      b.status !== "cancelled"
    );
  const bookingStartAt = (lane_id, hour) =>
    bookings.find(b =>
      b.lane_id === lane_id &&
      Math.floor(timeToMins(b.start_time) / 60) === hour &&
      b.status !== "cancelled"
    ) || null;

  // Returns the booking that is blocking this lane via a conflict (e.g. full lane blocking a cage)
  const conflictingBookingAt = (lane_id, hour) => {
    const blockedBy = conflicts[String(lane_id)] || [];
    for (const conflictId of blockedBy) {
      const b = bookingAt(Number(conflictId), hour);
      if (b) return b;
    }
    return null;
  };

  // Grouped by type (Full Lane / Cage / Box, the same grouping Facility
  // Config uses) so the header reads as a tree — a shared group label
  // spanning its columns, then each lane's own name underneath — instead
  // of a flat, ungrouped row of "Lane 1…Lane 5" columns with no visual
  // relationship to the Lanes/Cages/Boxes structure admins actually
  // configure. Lanes within a type keep their original relative order;
  // only the type buckets themselves are placed in this fixed order.
  const LANE_TYPE_ORDER = ["full_lane", "cage", "box"];
  const visibleLanesUnsorted = lanes.filter(l => l.active !== 0 && l.active !== false);
  const visibleLanes = LANE_TYPE_ORDER.flatMap(t => visibleLanesUnsorted.filter(l => l.lane_type === t))
    .concat(visibleLanesUnsorted.filter(l => !LANE_TYPE_ORDER.includes(l.lane_type)));
  const laneGroups = LANE_TYPE_ORDER
    .map(t => ({ type: t, lanes: visibleLanes.filter(l => l.lane_type === t) }))
    .filter(g => g.lanes.length > 0);

  // Columns actually rendered in the grid body — a collapsed group's lanes
  // drop out of the grid entirely (their group header stays, shrunk to a
  // single collapsed column) but remain fully bookable: BookingModal and the
  // empty-state check below always use the uncollapsed `visibleLanes`.
  const displayLanes = visibleLanes.filter(l => !collapsedTypes[l.lane_type]);

  // ── theme-derived grid colours
  const BORDER   = isDark ? "#1e2d42" : "#e2e8f0";
  const HDR_BG   = isDark ? "#0d1726" : "#f0f4f8";
  const CELL_BG  = isDark ? "#0a1322" : "#ffffff";
  const HOVER_BG = isDark ? "#111f33" : "#f1f5f9";
  const TIME_W   = 76;
  const CELL_H   = 72;
  const GROUP_ROW_H = 34;

  const isToday  = selDate === isoToday();
  const nowHour  = nowTick.getHours();

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%",
                  fontFamily: FONT, background: CELL_BG }}>

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 22px",
                    borderBottom: `1px solid ${BORDER}`,
                    background: isDark
                      ? "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0))"
                      : "linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.6))",
                    backdropFilter: BLUR, WebkitBackdropFilter: BLUR, flexShrink: 0 }}>
        {/* Facility selector — only shown when there's an actual choice to
            make. A single-facility tenant (the common case) has nothing to
            switch between, so this used to render as a permanently-lit
            pill repeating the org name for no functional reason. When there
            IS more than one facility, use the same plain dropdown the rest
            of the module (Lane Calendar, Booking Management) already uses,
            instead of this screen's own bespoke pill-button treatment. */}
        {facilities.length > 1 && (
          <select value={selFacility?.facility_id || ""}
            onChange={e => setSelFacility(facilities.find(f => String(f.facility_id) === e.target.value))}
            style={{ padding: "7px 12px", border: `1px solid ${BORDER}`, borderRadius: 9,
                     cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: FONT,
                     background: T.inputBg, color: T.text }}>
            {facilities.map(f => (
              <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>
            ))}
          </select>
        )}

        {facilities.length > 1 && <div style={{ width: 1, height: 26, background: BORDER, marginLeft: 2 }} />}

        {/* Date nav — a single segmented control instead of 3 separate
            bordered buttons, for a tighter, more considered look. */}
        <div style={{ display: "flex", alignItems: "center", borderRadius: 10,
                      border: `1px solid ${BORDER}`, overflow: "hidden",
                      background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff" }}>
          <button onClick={() => navigate(-1)}
            style={{ width: 32, height: 32, borderTop: "none", borderLeft: "none", borderBottom: "none",
                     borderRight: `1px solid ${BORDER}`,
                     background: "none", color: T.text, cursor: "pointer",
                     display: "flex", alignItems: "center", justifyContent: "center",
                     transition: "background .12s" }}
            onMouseEnter={e => e.currentTarget.style.background = HOVER_BG}
            onMouseLeave={e => e.currentTarget.style.background = "none"}>
            <FaChevronLeft size={11} />
          </button>
          <button onClick={() => setSelDate(isoToday())}
            style={{ padding: "7px 16px", border: "none", background: "none", color: T.text,
                     fontSize: 13, fontWeight: 700, fontFamily: FONT, cursor: "pointer" }}>
            Today
          </button>
          <button onClick={() => navigate(1)}
            disabled={!isStaff && selDate >= maxDate}
            style={{ width: 32, height: 32, borderTop: "none", borderRight: "none", borderBottom: "none",
                     borderLeft: `1px solid ${BORDER}`,
                     background: "none", color: selDate >= maxDate && !isStaff ? T.textMuted : T.text,
                     cursor: !isStaff && selDate >= maxDate ? "not-allowed" : "pointer",
                     display: "flex", alignItems: "center", justifyContent: "center",
                     opacity: !isStaff && selDate >= maxDate ? 0.4 : 1, transition: "background .12s" }}
            onMouseEnter={e => { if (isStaff || selDate < maxDate) e.currentTarget.style.background = HOVER_BG; }}
            onMouseLeave={e => e.currentTarget.style.background = "none"}>
            <FaChevronRight size={11} />
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: T.text, letterSpacing: "-0.01em" }}>
            {friendlyDate(selDate)}
          </span>
          {isToday && (
            <span style={{ fontSize: 9, fontWeight: 800, color: getBrandColors().blue,
                           background: `${getBrandColors().blue}18`, borderRadius: 20,
                           padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              ● Live
            </span>
          )}
        </div>

        {/* Facility hours badge */}
        <div style={{ padding: "5px 13px", borderRadius: 20,
                      background: T.inputBg, border: `1px solid ${BORDER}`,
                      fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
          {hourLabel(openH)} – {hourLabel(closeH)}
          {isAdmin && " (Admin: extended hours)"}
        </div>

        {/* Client advance booking notice */}
        {!isStaff && (
          <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
            Bookings available: current week + next week
          </div>
        )}

        <button onClick={loadBookings} title="Refresh"
          style={{ marginLeft: "auto", width: 30, height: 30, border: `1px solid ${BORDER}`,
                   borderRadius: 8, background: "none", color: T.textMuted, cursor: "pointer",
                   display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FaSync size={11} style={{ animation: loading ? "spin 0.9s linear infinite" : "none" }} />
        </button>
      </div>

      {/* ── Scrollable grid ── */}
      <div style={{ flex: 1, overflowX: "auto", overflowY: "auto" }}>
        {/* Frame the grid explicitly — with border-collapse:separate (used
            below so sticky <th> borders render cleanly during scroll,
            avoiding a second class of collapse-vs-sticky rendering glitch),
            each cell only draws its own right/bottom edge, so the outer
            top/left edge needs this wrapper to close the frame. */}
        <table style={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed",
                        width: `${TIME_W + displayLanes.length * 160}px`, minWidth: "100%",
                        borderTop: `1px solid ${BORDER}`, borderLeft: `1px solid ${BORDER}` }}>
          <colgroup>
            <col style={{ width: TIME_W }} />
            {displayLanes.map(l => <col key={l.lane_id} style={{ width: 160 }} />)}
          </colgroup>

          {/* ── Lane header rows ──
              Sticky positioning lives on each <th> individually, not on the
              <thead> as a whole — putting `position:sticky` only on <thead>
              is the pattern that let scrolled booking content visually
              overlap/bleed through the header on scroll (a real cross-
              browser sticky-table quirk, worse here because the group row's
              tint was also only ~8% opaque). Every sticky <th> below now has
              a fully OPAQUE background and its own top offset, which is the
              robust, standards-safe way to pin a multi-row table header. */}
          <thead>
            {/* Group row (the tree) — only shown when there's more than one
                type on the floor; a single-type facility (e.g. all Full
                Lanes) has nothing to group. Click a group to collapse/expand
                its columns — purely a display convenience, every lane stays
                fully bookable via BookingModal regardless of collapse state. */}
            {laneGroups.length > 1 && (
              <tr>
                <th rowSpan={2} style={{ position: "sticky", top: 0, zIndex: 12,
                             background: HDR_BG, borderRight: `1px solid ${BORDER}`,
                             borderBottom: `1px solid ${BORDER}`, padding: "10px 8px" }} />
                {laneGroups.map(g => {
                  const gc = LANE_COLOR[g.type] || "#6366f1";
                  const collapsed = !!collapsedTypes[g.type];
                  return (
                    <th key={g.type} colSpan={collapsed ? 1 : g.lanes.length}
                      onClick={() => toggleGroup(g.type)}
                      style={{ position: "sticky", top: 0, zIndex: 11, height: GROUP_ROW_H,
                               background: HDR_BG, borderRight: `1px solid ${BORDER}`,
                               borderBottom: `2px solid ${gc}`, padding: "0 14px", textAlign: "left",
                               cursor: "pointer", userSelect: "none" }}
                      title={collapsed ? `Expand ${LANE_GROUP_LABEL[g.type]}` : `Collapse ${LANE_GROUP_LABEL[g.type]}`}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {collapsed
                          ? <FaChevronRight size={9} color={gc} />
                          : <FaChevronDown size={9} color={gc} />}
                        <span style={{ fontSize: 11, fontWeight: 800, color: gc,
                                       textTransform: "uppercase", letterSpacing: "0.04em",
                                       whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {LANE_GROUP_LABEL[g.type]} ({g.lanes.length})
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            )}
            <tr>
              {laneGroups.length <= 1 && (
                <th style={{ position: "sticky", top: 0, zIndex: 12,
                             background: HDR_BG, borderRight: `1px solid ${BORDER}`,
                             borderBottom: `1px solid ${BORDER}`, padding: "10px 8px" }} />
              )}
              {displayLanes.map(l => {
                const lc = l.color || LANE_COLOR[l.lane_type] || "#6366f1";
                return (
                  <th key={l.lane_id}
                    style={{ position: "sticky", top: laneGroups.length > 1 ? GROUP_ROW_H : 0, zIndex: 11,
                             background: HDR_BG, borderRight: `1px solid ${BORDER}`,
                             borderBottom: `1px solid ${BORDER}`,
                             padding: "10px 14px", textAlign: "left",
                             borderTop: `3px solid ${lc}`, boxShadow: `0 2px 4px rgba(0,0,0,${isDark ? 0.25 : 0.06})` }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: lc }}>{l.lane_name}</div>
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>
                      {LANE_LABEL[l.lane_type]}
                    </div>
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>
                      {shortDate(selDate)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* ── Time rows ── */}
          <tbody>
            {HOURS.map(hour => {
              const isNowRow = isToday && hour === nowHour;
              return (
              <tr key={hour}>
                <td style={{ background: HDR_BG, borderRight: `1px solid ${BORDER}`,
                             borderBottom: `1px solid ${BORDER}`,
                             padding: "0 10px", height: CELL_H, verticalAlign: "top",
                             paddingTop: 8, position: "relative" }}>
                  {isNowRow && (
                    <span style={{ position: "absolute", left: 4, top: 10, width: 6, height: 6,
                                   borderRadius: "50%", background: getBrandColors().blue,
                                   boxShadow: `0 0 0 3px ${getBrandColors().blue}33` }} />
                  )}
                  <span style={{ fontSize: 12, fontWeight: isNowRow ? 800 : 600,
                                 color: isNowRow ? getBrandColors().blue : T.textMuted,
                                 whiteSpace: "nowrap", marginLeft: isNowRow ? 10 : 0 }}>
                    {hourLabel(hour)}
                  </span>
                </td>

                {displayLanes.map(lane => {
                  const existing   = bookingAt(lane.lane_id, hour);
                  const startBlock = bookingStartAt(lane.lane_id, hour);
                  const lc = lane.color || LANE_COLOR[lane.lane_type] || "#6366f1";

                  // middle of multi-hour booking → skip (rowSpan handles it)
                  if (existing && !startBlock) return null;

                  if (startBlock) {
                    const startMins = timeToMins(startBlock.start_time);
                    const endMins   = timeToMins(startBlock.end_time);
                    const spanHours = Math.max(1, Math.ceil((endMins - startMins) / 60));
                    const sc        = STATUS_COLOR[startBlock.status] || lc;

                    return (
                      <td key={lane.lane_id} rowSpan={spanHours}
                        style={{ borderRight: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`,
                                 padding: 5, verticalAlign: "top", cursor: "pointer",
                                 background: isNowRow ? `${getBrandColors().blue}08` : "transparent" }}
                        onClick={() => setModal({ date: selDate, lane, hour, viewBooking: startBlock })}>
                        <div style={{ background: `linear-gradient(160deg, ${sc}26, ${sc}12)`,
                                      borderTop: `1px solid ${sc}45`, borderRight: `1px solid ${sc}45`,
                                      borderBottom: `1px solid ${sc}45`, borderLeft: `4px solid ${sc}`,
                                      borderRadius: 10, padding: "8px 11px",
                                      height: `calc(${CELL_H * spanHours}px - 16px)`,
                                      boxSizing: "border-box", overflow: "hidden",
                                      boxShadow: `0 1px 3px rgba(0,0,0,${isDark ? 0.3 : 0.06})`,
                                      transition: "transform .12s, box-shadow .12s" }}
                          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 4px 10px rgba(0,0,0,${isDark ? 0.4 : 0.12})`; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 1px 3px rgba(0,0,0,${isDark ? 0.3 : 0.06})`; }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.text,
                                        fontFamily: FONT }}>
                            {startBlock.client_name || "Academy"}
                          </div>
                          <div style={{ fontSize: 10, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
                            Lane Booking
                          </div>
                          <div style={{ marginTop: 5 }}>
                            <span style={{ fontSize: 9, fontWeight: 800, color: sc,
                                           background: `${sc}22`, borderRadius: 4,
                                           padding: "2px 6px", textTransform: "uppercase",
                                           fontFamily: FONT }}>
                              {startBlock.status?.replace(/_/g," ")}
                            </span>
                          </div>
                        </div>
                      </td>
                    );
                  }

                  // check if blocked by a conflicting lane booking
                  const conflictSrc = conflictingBookingAt(lane.lane_id, hour);
                  if (conflictSrc) {
                    const csc = STATUS_COLOR[conflictSrc.status] || "#6b7280";
                    return (
                      <td key={lane.lane_id}
                        title={`Blocked — ${conflictSrc.lane_name} is booked (${conflictSrc.client_name || "Academy"})`}
                        style={{ borderRight: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`,
                                 padding: 5, height: CELL_H, verticalAlign: "top",
                                 background: isDark
                                   ? "repeating-linear-gradient(45deg,#0d1726,#0d1726 6px,#121e30 6px,#121e30 12px)"
                                   : "repeating-linear-gradient(45deg,#f8fafc,#f8fafc 6px,#eef2f7 6px,#eef2f7 12px)",
                                 cursor: "not-allowed" }}>
                        <div style={{ height: "100%", display: "flex", flexDirection: "column",
                                      alignItems: "center", justifyContent: "center", gap: 3 }}>
                          <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase",
                                        letterSpacing: "0.06em", color: csc, fontFamily: FONT }}>
                            Blocked
                          </div>
                          <div style={{ fontSize: 9, color: T.textMuted, fontFamily: FONT,
                                        textAlign: "center", lineHeight: 1.3 }}>
                            {conflictSrc.lane_name}
                          </div>
                        </div>
                      </td>
                    );
                  }

                  // empty cell — bookable
                  return (
                    <td key={lane.lane_id}
                      style={{ borderRight: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`,
                               padding: 0, height: CELL_H,
                               background: isNowRow ? `${getBrandColors().blue}08` : CELL_BG }}
                      onMouseEnter={e => e.currentTarget.style.background = HOVER_BG}
                      onMouseLeave={e => e.currentTarget.style.background = isNowRow ? `${getBrandColors().blue}08` : CELL_BG}>
                      <div style={{ width: "100%", height: "100%",
                                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <button
                          onClick={() => setModal({ date: selDate, lane, hour })}
                          style={{
                            padding: "6px 18px", border: `1px solid ${BORDER}`,
                            borderRadius: 20, background: "none", color: T.textMuted,
                            fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: "pointer",
                            transition: "all .15s",
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = lc;
                            e.currentTarget.style.color = "#fff";
                            e.currentTarget.style.border = `1px solid ${lc}`;
                            e.currentTarget.style.boxShadow = `0 3px 10px ${lc}55`;
                            e.currentTarget.style.transform = "scale(1.04)";
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = "none";
                            e.currentTarget.style.color = T.textMuted;
                            e.currentTarget.style.border = `1px solid ${BORDER}`;
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "scale(1)";
                          }}>
                          Reserve
                        </button>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );})}
          </tbody>
        </table>

        {visibleLanes.length === 0 && (
          <div style={{ padding: "80px 40px", textAlign: "center",
                        color: T.textMuted, fontSize: 14, fontFamily: FONT }}>
            No lanes configured for this facility.
          </div>
        )}
      </div>

      {/* ── New booking modal ── */}
      {modal && !modal.viewBooking && (
        <BookingModal
          cell={modal} lanes={visibleLanes}
          bookingTypes={bookingTypes} facility={selFacility}
          T={T} currentUser={currentUser}
          onClose={() => setModal(null)} onBookingCreated={handleBookingCreated}
        />
      )}

      {/* ── View existing booking ── */}
      {modal?.viewBooking && (
        <ViewBookingModal
          booking={modal.viewBooking} T={T}
          onClose={() => setModal(null)} onUpdateStatus={handleStatusUpdate}
        />
      )}

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
